import { FC, HTMLAttributes, ReactNode, useCallback, useState } from "react"

import * as RToast from "@radix-ui/react-toast"
import clsx from "clsx"

import ErrorWithData from "@/utils/ErrorWithData"
import { s } from "@/utils/i18n"

import ErrorUnwrapper from "@/components/ErrorUnwrapper"
import { P3 } from "@/components/TypeRamp"

// import CancelIcon from "@/icons/Cancel"
// import CheckCircleIcon from "@/icons/CheckCircle"
// import ErrorCircleIcon from "@/icons/ErrorCircle"
import CloseIcon from "@/icons/Close"

const SOFT_CLOSE_TIMEOUT = 500

const TOAST_STYLES = {
  success: "bg-black text-white",
  error: "bg-black text-white",
  none: "bg-black text-white",
}

// const TOAST_ICONS: Record<string, FC<SVGProps<SVGSVGElement>>> = {
//   success: (props) => <CheckCircleIcon style={{ color: "#90ee4c" }} {...props} />,
//   error: (props) => <ErrorCircleIcon style={{ color: "#fd6f8e" }} {...props} />,
//   none: () => null,
// }

type ManualToastContent = { message: ReactNode; detail?: string | ReactNode; permanent?: boolean }

export type ToastContent = {
  content: string | ErrorWithData | ManualToastContent
}

export type ToastProps = {
  type?: keyof typeof TOAST_STYLES
} & ToastContent

export const Toast: FC<ToastProps & Omit<HTMLAttributes<HTMLLIElement>, "content"> & { close: () => void }> = ({
  type = "success",
  className = "",
  close,
  ...props
}) => {
  const [isClosing, setIsClosing] = useState(false)
  // const Icon = TOAST_ICONS[type]
  const softClose = useCallback(
    (e?: MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }
      setIsClosing(true)
      setTimeout(close, SOFT_CLOSE_TIMEOUT)
    },
    [close]
  )
  return (
    <RToast.Root
      duration={"permanent" in props && props.permanent ? Infinity : undefined}
      className={clsx(
        className,
        TOAST_STYLES[type],
        "px-3 py-2",
        "flex items-center",
        "animate-slide-in",
        isClosing && "translate-y-full opacity-0",
        "transition duration-500",
        "rounded-lg"
      )}
      style={{ width: "20rem", maxWidth: "calc(100vw - 3rem)" }}
      forceMount
      onOpenChange={(open) => {
        if (open) {
          return
        }
        softClose()
      }}
    >
      {/* <Icon width="24" height="24" className="mr-3 shrink-0" /> */}
      <div className="grow">
        <RToast.Title asChild>
          <P3 as="div">
            {(typeof props.content === "string" && props.content) ||
              (typeof props.content === "object" && props.content.message)}
          </P3>
        </RToast.Title>
        <RToast.Description>
          {typeof props?.content === "object" && "detail" in props.content && props.content.detail && (
            <P3>{props.content.detail}</P3>
          )}
          {props.content instanceof ErrorWithData && props.content?.data && typeof props.content?.data === "object" && (
            <P3 as="div">
              <ErrorUnwrapper error={props.content.data} />
            </P3>
          )}
        </RToast.Description>
      </div>
      {!("permanent" in props && props.permanent) && (
        <RToast.Close className="ml-3 flex shrink-0 cursor-pointer p-3">
          <span className="sr-only">{s`Dismiss notification`}</span>
          <CloseIcon width="18" height="18" />
          {/* <CancelIcon width="18" height="18" /> */}
        </RToast.Close>
      )}
    </RToast.Root>
  )
}

export default Toast
